import { cn } from '@/lib/utils'

export interface BarChartSeries {
  readonly key: string
  readonly label: string
  /** Classe de cor de fundo do segmento (ex.: 'bg-status-success'). */
  readonly colorClassName: string
}

export interface BarChartPoint {
  readonly key: string
  /** Rótulo do eixo X (ex.: '18/08'). */
  readonly label: string
  /** Valor por série, indexado por `series.key`. */
  readonly values: Readonly<Record<string, number>>
}

export interface BarChartProps {
  readonly series: readonly BarChartSeries[]
  readonly points: readonly BarChartPoint[]
  readonly ariaLabel: string
  readonly formatValue?: (value: number) => string
  /** Altura da área de plotagem em px (default 160). */
  readonly height?: number
  /** Quantidade aproximada de rótulos no eixo X (default 8). */
  readonly maxLabels?: number
}

function pointTotal(point: BarChartPoint, series: readonly BarChartSeries[]): number {
  return series.reduce((sum, s) => sum + (point.values[s.key] ?? 0), 0)
}

/**
 * Gráfico de barras empilhadas feito só com CSS (flex + alturas percentuais) — responsivo,
 * theme-aware pelos tokens e sem dependência externa. Acessível: a área visual é `role="img"` com
 * um resumo em `aria-label`, e há uma tabela `sr-only` com os números por período para leitores de
 * tela. Cada valor mapeia um dado real (nunca decorativo).
 */
export function BarChart({
  series,
  points,
  ariaLabel,
  formatValue = (value) => value.toString(),
  height = 160,
  maxLabels = 8,
}: BarChartProps) {
  const maxTotal = points.reduce((max, point) => Math.max(max, pointTotal(point, series)), 0)
  const labelStep = Math.max(1, Math.ceil(points.length / maxLabels))

  return (
    <figure className="flex flex-col gap-2">
      <div
        role="img"
        aria-label={ariaLabel}
        className="flex items-end gap-1"
        style={{ height: `${height.toString()}px` }}
      >
        {points.map((point) => {
          const titleParts = series
            .filter((s) => (point.values[s.key] ?? 0) > 0)
            .map((s) => `${s.label}: ${formatValue(point.values[s.key] ?? 0)}`)
          return (
            <div
              key={point.key}
              title={`${point.label} — ${titleParts.length > 0 ? titleParts.join(', ') : formatValue(0)}`}
              className="flex h-full flex-1 flex-col justify-end gap-px"
            >
              {maxTotal === 0
                ? null
                : series
                    .filter((s) => (point.values[s.key] ?? 0) > 0)
                    .map((s, index) => (
                      <div
                        key={s.key}
                        className={cn(
                          s.colorClassName,
                          index === 0 ? 'rounded-t-sm' : '',
                          'min-h-[2px] w-full',
                        )}
                        style={{
                          height: `${(((point.values[s.key] ?? 0) / maxTotal) * 100).toString()}%`,
                        }}
                      />
                    ))}
            </div>
          )
        })}
      </div>

      <div className="flex gap-1">
        {points.map((point, index) => (
          <span
            key={point.key}
            className="flex-1 truncate text-center text-[10px] text-foreground-muted"
          >
            {index % labelStep === 0 ? point.label : ''}
          </span>
        ))}
      </div>

      {series.length > 1 ? (
        <div className="flex flex-wrap gap-3">
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5 text-xs text-foreground-muted">
              <span aria-hidden="true" className={cn('h-2.5 w-2.5 rounded-sm', s.colorClassName)} />
              {s.label}
            </span>
          ))}
        </div>
      ) : null}

      {/* Tabela equivalente para leitores de tela (a área visual é role=img). */}
      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th>#</th>
            {series.map((s) => (
              <th key={s.key}>{s.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {points.map((point) => (
            <tr key={point.key}>
              <th scope="row">{point.label}</th>
              {series.map((s) => (
                <td key={s.key}>{formatValue(point.values[s.key] ?? 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  )
}
